import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "../api";
import { ApiError } from "../api";
import { Ic } from "../components/ui";
import { useNumeProiect } from "../components/NumeProiect";
import { useFormat, useT } from "../i18n";
import { useRegistruOptional } from "../nav/Registru";
import { elementeStructura } from "../nav/selectie";

/** Datele proiectului ca OBIECT: cum se numește, al cui e, când s-a făcut, și
 *  scoaterea lui din listă.
 *
 *  Redenumirea există și în „Dosarul proiectului", pe titlu — acolo e la
 *  îndemână, fiindcă acolo stau actele din care se află numele adevărat.
 *  Aici e locul în care o CAUȚI când nu știi unde e, iar cele două scriu în
 *  același loc (`useNumeProiect`), deci nu pot să divergă. */

export default function General({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  const t = useT();
  const { data } = useFormat();
  const { nume, redenumeste } = useNumeProiect(projectId);
  const [proiect, setProiect] = useState<api.Project | null>(null);
  const [text, setText] = useState("");
  const [salvat, setSalvat] = useState(false);
  const [ocupat, setOcupat] = useState(false);
  const [eroare, setEroare] = useState("");
  const [mesaj, setMesaj] = useState("");

  useEffect(() => {
    let viu = true;
    api.getProject(projectId)
      .then((p) => viu && setProiect(p))
      .catch(() => undefined);
    return () => { viu = false; };
  }, [projectId]);

  useEffect(() => { if (nume !== null) setText(nume); }, [nume]);

  // Numele și arhivarea cer drept de scriere, nu `admin`: decizia echipei
  // e că oricine are acces pe proiect poate face orice pe el.
  const poate = proiect?.role === "editor" || proiect?.role === "admin";

  async function salveaza(e: React.FormEvent) {
    e.preventDefault();
    const nou = text.trim();
    if (!nou || nou === nume) return;
    setOcupat(true);
    setEroare("");
    try {
      await redenumeste(nou);
      setSalvat(true);
    } catch (err) {
      setEroare(err instanceof ApiError && err.message
        ? err.message : t("nume_proiect.esec"));
    } finally {
      setOcupat(false);
    }
  }

  // «ȘTERGE TOATE SOLUȚIILE» a venit aici din pagina soluțiilor (contract
  // §5.3): e o ștergere a tot ce a produs platforma, nu o unealtă de lucru, iar
  // lângă «Oprește tot» se apăsa din greșeală. Dosarul, actele citite și
  // preferințele rămân; schițele pornesc singure din nou pe arhitecturi (D11).
  //
  // Numărătoarea e a RÂNDURILOR, cu aceeași regulă ca defalcarea: la structură
  // Soluția N (macro + detaliu) e una, iar rulările fără rând nu sunt soluții.
  // Altfel «toate cele 9» nu se aduna din «2 amplasări, 2 arhitecturi, 2
  // schițe, 2 soluții».
  const reg = useRegistruOptional();
  const randuri = reg?.registru?.solutii ?? [];
  const elemente = elementeStructura(randuri);
  const nr = {
    situatie: randuri.filter((s) => s.discipline === "site_plan").length,
    arhitectura: randuri.filter((s) => s.discipline === "architecture").length,
    schite: elemente.filter((e) => e.fel === "schita").length,
    solutii: elemente.filter((e) => e.fel === "solutie").length,
  };
  const nrSolutii = nr.situatie + nr.arhitectura + nr.schite + nr.solutii;
  async function stergeSolutiile() {
    if (!reg?.registru) return;
    if (!confirm(t("setari.confirma_sterge_solutii", { n: nrSolutii, ...nr }))) return;
    setOcupat(true);
    setEroare("");
    setMesaj("");
    try {
      const r = await api.deleteAllSolutions(projectId, "all");
      await reg.reciteste();
      setMesaj(t("setari.sterse_solutii", { solutii: r.solutii, joburi: r.joburi,
                                           artefacte: r.artefacte }));
    } catch (err) {
      setEroare(err instanceof ApiError && err.message ? err.message
        : t("setari.esec_sterge_solutii"));
    } finally {
      setOcupat(false);
    }
  }

  async function arhiveaza() {
    if (!proiect) return;
    if (!confirm(t("setari.confirma_arhivare", { proiect: proiect.name }))) return;
    setOcupat(true);
    setEroare("");
    try {
      await api.archiveProject(projectId);
      navigate("/projects");
    } catch (err) {
      setEroare(t(err instanceof ApiError && err.code === "forbidden"
        ? "comun.doar_citire" : "setari.esec_arhivare"));
      setOcupat(false);
    }
  }

  if (proiect === null) {
    return (
      <div className="setari-pagina">
        <p className="note">{t("comun.se_incarca")}</p>
      </div>
    );
  }

  return (
    <div className="setari-pagina">
      <div className="setari-titlu"><h2 className="disp">{t("setari.general")}</h2></div>

      {eroare && <p className="note bad setari-eroare">{eroare}</p>}
      {mesaj && (
        <p className="note setari-eroare" role="status">
          <Ic n="check" s={12} /> {mesaj}
        </p>
      )}
      {!poate && (
        <p className="note setari-eroare">{t("setari.general_doar_citire")}</p>
      )}

      <h3 className="setari-sectiune">{t("setari.numele")}</h3>
      <form className="row-form" onSubmit={salveaza}>
        <input value={text} disabled={!poate || ocupat}
               aria-label={t("nume_proiect.camp")}
               onChange={(e) => { setText(e.target.value); setSalvat(false); }} />
        <button className="btn btnp" type="submit"
                disabled={!poate || ocupat || !text.trim() || text.trim() === nume}>
          <Ic n="check" s={14} />{t(ocupat ? "comun.se_salveaza" : "comun.salveaza")}
        </button>
        {salvat && (
          <span className="setari-salvat">
            <Ic n="check" s={12} />{t("comun.salvat")}
          </span>
        )}
      </form>

      <h3 className="setari-sectiune">{t("setari.despre")}</h3>
      <dl className="setari-fapte">
        <div>
          <dt>{t("setari.organizatie")}</dt><dd>{proiect.organization.name}</dd>
        </div>
        <div><dt>{t("setari.creat")}</dt><dd>{cand(proiect.created_at)}</dd></div>
        <div>
          <dt>{t("setari.modificat")}</dt><dd>{cand(proiect.updated_at)}</dd>
        </div>
      </dl>

      <h3 className="setari-sectiune setari-sectiune-rea">{t("setari.arhivare")}</h3>
      <div className="setari-periculos">
        <div>
          <b>{t("setari.sterge_solutii_titlu")}</b>
          <p className="note">{t("setari.sterge_solutii_text")}</p>
        </div>
        <div className="sp" />
        <button className="btn setari-buton-rau" disabled={!poate || ocupat || nrSolutii === 0}
                onClick={stergeSolutiile}>{t("setari.sterge_solutii")}</button>
      </div>
      <div className="setari-periculos">
        <div>
          <b>{t("setari.arhivare_titlu")}</b>
          <p className="note">{t("setari.arhivare_text")}</p>
        </div>
        <div className="sp" />
        <button className="btn setari-buton-rau" disabled={!poate || ocupat}
                onClick={arhiveaza}>{t("setari.arhiveaza")}</button>
      </div>
    </div>
  );

  function cand(iso: string | null): string {
    return data(iso, { day: "numeric", month: "long", year: "numeric" });
  }
}
